import React, { useMemo } from 'react';

// Time-of-day sky color grades (layered over the static base land illustration).
const PHASE_SKY = {
  dawn:    'linear-gradient(180deg, rgba(255,142,110,0.60) 0%, rgba(255,196,140,0.20) 42%, rgba(8,12,28,0.32) 100%)',
  morning: 'linear-gradient(180deg, rgba(150,196,240,0.42) 0%, rgba(214,236,255,0.12) 42%, rgba(8,12,28,0.28) 100%)',
  midday:  'linear-gradient(180deg, rgba(255,255,255,0.16) 0%, rgba(140,206,255,0.06) 42%, rgba(8,12,28,0.24) 100%)',
  dusk:    'linear-gradient(180deg, rgba(150,44,145,0.60) 0%, rgba(233,74,97,0.34) 48%, rgba(246,132,53,0.22) 100%)',
  night:   'linear-gradient(180deg, rgba(12,18,42,0.55) 0%, rgba(16,24,48,0.42) 45%, rgba(6,9,22,0.58) 100%)',
};

const SUN = {
  dawn:    { top: '58%', left: '22%', size: 150, c: '#FFB27A', glow: '#FF8E6E' },
  morning: { top: '20%', left: '24%', size: 120, c: '#FFE8B0', glow: '#FFD27A' },
  midday:  { top: '12%', left: '50%', size: 132, c: '#FFFDF5', glow: '#FFF0C0' },
  dusk:    { top: '56%', left: '76%', size: 160, c: '#FF9A5A', glow: '#E9506A' },
};

const Cloud = ({ top, dur, delay, scale, opacity }) => (
  <div className="absolute rounded-full bg-white blur-2xl" style={{
    top, left: '-22%', width: 220 * scale, height: 68 * scale, opacity,
    animation: `dist-cloud ${dur}s linear ${delay}s infinite`,
  }} />
);

const DistrictAtmosphere = ({ phase = 'midday', weather = 'clear' }) => {
  const isNight = phase === 'night';
  const sun = SUN[phase];
  const clouds = ['partly-cloudy', 'overcast', 'crisp'].includes(weather);

  const stars = useMemo(() => Array.from({ length: 26 }).map((_, i) => ({
    top: `${(i * 37) % 55}%`, left: `${(i * 53) % 100}%`, d: (i % 5) * 0.4, s: 1 + (i % 3),
  })), []);

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden data-testid="district-atmosphere">
      {/* Time-of-day color grade */}
      <div className="absolute inset-0 transition-[background] duration-1000"
        style={{ background: PHASE_SKY[phase], mixBlendMode: isNight ? 'normal' : 'soft-light' }} />
      {isNight && (
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(6,9,22,0.34) 0%, rgba(6,9,22,0.14) 50%, rgba(6,9,22,0.40) 100%)' }} />
      )}

      {/* Sun */}
      {sun && (
        <div className="absolute rounded-full" style={{
          top: sun.top, left: sun.left, width: sun.size, height: sun.size,
          transform: 'translate(-50%,-50%)',
          background: `radial-gradient(circle, ${sun.c} 0%, ${sun.glow}77 40%, transparent 70%)`,
          animation: 'dist-sun-pulse 8s ease-in-out infinite',
        }} />
      )}

      {/* Moon + stars */}
      {isNight && (
        <>
          <div className="absolute rounded-full" style={{
            top: '15%', right: '18%', width: 74, height: 74,
            background: 'radial-gradient(circle at 38% 38%, #F4F6FF 0%, #C9D3EE 55%, transparent 72%)',
            boxShadow: '0 0 42px rgba(200,214,255,0.5)',
          }} />
          {stars.map((st, i) => (
            <span key={i} className="absolute rounded-full bg-white" style={{
              top: st.top, left: st.left, width: st.s, height: st.s,
              animation: `dist-twinkle ${2.4 + st.d}s ease-in-out ${st.d}s infinite`,
            }} />
          ))}
        </>
      )}

      {/* God rays on a clear daytime */}
      {weather === 'clear' && !isNight && (
        <div className="absolute inset-0" style={{
          background: 'conic-gradient(from 200deg at 50% -8%, transparent 0deg, rgba(255,244,200,0.14) 12deg, transparent 24deg, rgba(255,244,200,0.10) 40deg, transparent 60deg)',
          animation: 'dist-godray 9s ease-in-out infinite',
        }} />
      )}

      {/* Clouds */}
      {clouds && (
        <>
          <Cloud top="12%" dur={72} delay={0} scale={1.1} opacity={weather === 'overcast' ? 0.5 : 0.3} />
          <Cloud top="24%" dur={96} delay={12} scale={1.5} opacity={weather === 'overcast' ? 0.55 : 0.26} />
          <Cloud top="6%" dur={120} delay={30} scale={0.9} opacity={weather === 'overcast' ? 0.4 : 0.2} />
        </>
      )}
      {weather === 'overcast' && <div className="absolute inset-0 bg-slate-900/12" />}
      {weather === 'crisp' && <div className="absolute inset-0" style={{ background: 'rgba(200,225,255,0.06)' }} />}

      {/* Light rain */}
      {weather === 'light-rain' && (
        <>
          <div className="absolute inset-0 bg-slate-900/12" />
          <Cloud top="10%" dur={60} delay={0} scale={1.4} opacity={0.5} />
          <div className="absolute inset-0 opacity-60" style={{
            backgroundImage: 'repeating-linear-gradient(102deg, transparent 0 7px, rgba(190,214,255,0.35) 7px 8px)',
            backgroundSize: '100% 26px', animation: 'dist-rain 0.5s linear infinite',
          }} />
        </>
      )}

      {/* Fog */}
      {weather === 'fog' && [0, 1, 2].map((i) => (
        <div key={i} className="absolute blur-2xl bg-white/25" style={{
          left: 0, right: 0, height: 120, top: `${30 + i * 22}%`,
          animation: `dist-fog ${16 + i * 4}s ease-in-out ${i * 2}s infinite alternate`,
        }} />
      ))}
    </div>
  );
};

export default DistrictAtmosphere;
